import { useState } from "react";
import { Share2, Copy, Download, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const TEMA = [
  { id: "oversikt", namn: "Økonomien" },
  { id: "bemanning", namn: "Bemanning" },
  { id: "gjeld", namn: "Gjeld og renter" },
  { id: "skatt", namn: "Skatt og gebyr" },
  { id: "ulikskap", namn: "Ulikskap" },
];

export const DelKnapp = ({ kommuneCode, kommuneNamn, tema = "oversikt", kompakt = false, testId = "del" }) => {
  const [open, setOpen] = useState(false);
  const [valt, setValt] = useState(tema);
  const [kopiert, setKopiert] = useState(false);
  const bilete = `${API}/api/del/${kommuneCode}/${valt}.png`;
  const lenke = `${API}/api/del/${kommuneCode}/${valt}`;
  const tekst = `${kommuneNamn} kommune – tala frå Kommuneopprøret`;

  const kopier = async () => {
    try {
      await navigator.clipboard.writeText(lenke);
      setKopiert(true);
      toast.success("Lenka er kopiert – lim den inn der du vil dele");
      setTimeout(() => setKopiert(false), 2500);
    } catch {
      toast.error("Klarte ikkje kopiere lenka");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setValt(tema); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="bg-white border-[#E52437]/30 text-[#E52437] hover:bg-[#E52437]/5 hover:text-[#E52437] gap-1.5"
          data-testid={`${testId}-knapp`}
        >
          <Share2 className="w-3.5 h-3.5" />
          {kompakt ? "" : "Del"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-white" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Del tala for {kommuneNamn}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {TEMA.map((t) => (
              <button
                key={t.id}
                onClick={() => setValt(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                  valt === t.id ? "bg-[#E52437] text-white" : "bg-[#F1F4F8] text-[#4B647F] hover:bg-[#E6E9F0]"
                }`}
                data-testid={`${testId}-tema-${t.id}`}
              >
                {t.namn}
              </button>
            ))}
          </div>
          <img
            src={bilete}
            alt={`Delebilete for ${kommuneNamn}: ${valt}`}
            className="w-full rounded-lg border border-[#E6E9F0]"
            data-testid={`${testId}-bilete`}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={kopier} className="bg-[#E52437] text-white hover:bg-[#c41d2d] hover:text-white gap-1.5" data-testid={`${testId}-kopier`}>
              {kopiert ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              Kopier lenke
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 bg-white text-[#1F2A36] hover:bg-[#F1F4F8] hover:text-[#1F2A36]" data-testid={`${testId}-last-ned`}>
              <a href={bilete} download={`${kommuneNamn}-${valt}.png`}>
                <Download className="w-3.5 h-3.5" /> Last ned biletet
              </a>
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 bg-white text-[#1F2A36] hover:bg-[#F1F4F8] hover:text-[#1F2A36]" data-testid={`${testId}-facebook`}>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(lenke)}`} target="_blank" rel="noopener noreferrer">
                Facebook <ExternalLink className="w-3 h-3" />
              </a>
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 bg-white text-[#1F2A36] hover:bg-[#F1F4F8] hover:text-[#1F2A36]" data-testid={`${testId}-x`}>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tekst)}&url=${encodeURIComponent(lenke)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                X <ExternalLink className="w-3 h-3" />
              </a>
            </Button>
          </div>
          <p className="text-xs text-[#8AA4BF]">
            Biletet blir laga automatisk frå dei nyaste tala, i Raudt sin profilfarge. Lenka viser biletet i Facebook, X og LinkedIn, og sender folk
            vidare til kommunesida.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DelTopplisteKnapp = ({ liste, tittel, spor: sporInn = "", testId = "del-toppliste" }) => {
  const [open, setOpen] = useState(false);
  const spor = sporInn ? `?${sporInn}` : "";
  const bilete = `${API}/api/del-toppliste/${liste}.png${spor}`;
  const lenke = `${API}/api/del-toppliste/${liste}${spor}`;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="bg-white border-[#E52437]/30 text-[#E52437] hover:bg-[#E52437]/5 hover:text-[#E52437] gap-1.5"
          data-testid={`${testId}-knapp`}
        >
          <Share2 className="w-3.5 h-3.5" /> Del
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-white" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Del lista «{tittel}»</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <img src={bilete} alt={`Delebilete: ${tittel}`} className="w-full rounded-lg border border-[#E6E9F0]" data-testid={`${testId}-bilete`} />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-[#E52437] text-white hover:bg-[#c41d2d] hover:text-white gap-1.5"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(lenke);
                  toast.success("Lenka er kopiert");
                } catch {
                  toast.error("Klarte ikkje kopiere lenka");
                }
              }}
              data-testid={`${testId}-kopier`}
            >
              <Copy className="w-3.5 h-3.5" /> Kopier lenke
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 bg-white text-[#1F2A36] hover:bg-[#F1F4F8] hover:text-[#1F2A36]" data-testid={`${testId}-last-ned`}>
              <a href={bilete} download={`${liste}.png`}>
                <Download className="w-3.5 h-3.5" /> Last ned biletet
              </a>
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 bg-white text-[#1F2A36] hover:bg-[#F1F4F8] hover:text-[#1F2A36]" data-testid={`${testId}-facebook`}>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(lenke)}`} target="_blank" rel="noopener noreferrer">
                Facebook <ExternalLink className="w-3 h-3" />
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DelKnapp;
